import {
  getUserBillingStatus,
  getUserVerificationStatus,
} from "@/lib/utils/accessChecks";
import { getRawIconForResource } from "@/lib/utils/icons";
import { getLoginUrl } from "@/lib/utils/routing";
import {
  Badge,
  Box,
  Button,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { HandymanRounded } from "@mui/icons-material";
import LockRounded from "@mui/icons-material/LockRounded";
import { Resources } from "@tsTypes/subject-schema";
import clsx from "clsx";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { CSSProperties, Fragment } from "react";
import { getResourceNiceName } from "./IndividualResourceCard";
import classes from "./ResourceNotAvailablePlaceholder.module.css";

export function ResourceNotAvailablePlaceholder({
  reason,
  resource,
  subjectColorIndex,
}: {
  reason: "locked" | "in development" | "loading";
  resource: Extract<
    Resources,
    | "cram sheets"
    | "topic tests"
    | "questions"
    | "videos"
    | "ia examples"
    | "flashcards"
    | "notes"
  >;
  subjectColorIndex: string;
}) {
  const Icon = getRawIconForResource(resource);
  const theme = useMantineTheme();
  const { data: session } = useSession();
  const iconMultiplier =
    resource === "notes" ? 2 : resource === "flashcards" ? 1.5 : 1;
  const scaleMultiplier = resource === "notes" ? 2 : 1;
  const userUnlockResourceAccessDescriptor =
    getUserUnlockResourceAccessDescriptor(
      session,
      getUserVerificationStatus(session)
    );
  const ctaHref = getUserUnlockResourceCTAHref(
    userUnlockResourceAccessDescriptor
  );
  const isLoginCTA = userUnlockResourceAccessDescriptor === "login to unlock";

  const iconColor = theme.colors.dark[2];

  const overlayStyle = {
    "--scale-multiplier": scaleMultiplier,
  } as CSSProperties;
  const patternStyle = {
    "--scale-multiplier": scaleMultiplier,
    "--icon-multiplier": iconMultiplier,
  } as CSSProperties;

  return (
    <>
      <Box className={classes.overlayBox} style={overlayStyle}>
        {reason === "loading" ? (
          <Loader
            className={classes.mainIcon}
            color={iconColor}
            type="dots"
          />
        ) : reason === "locked" ? (
          <LockRounded className={classes.mainIcon} htmlColor={iconColor} />
        ) : (
          <HandymanRounded className={classes.mainIcon} htmlColor={iconColor} />
        )}
        {reason !== "in development" ? (
          <Button
            component={Link}
            href={ctaHref}
            onClick={(event) => {
              if (isLoginCTA) {
                event.preventDefault();
                if (typeof window !== "undefined") {
                  window.location.href = getLoginUrl();
                }
              }
            }}
            variant="filled"
            size="compact-sm"
            className={clsx(classes.cta, reason === "locked" && classes.ctaLocked)}
          >
            {reason === "loading"
              ? "Checking access..."
              : userUnlockResourceAccessDescriptor}
          </Button>
        ) : (
          <Badge
            variant="filled"
            className={classes.cta}
            size={scaleMultiplier ? "xl" : "md"}
          >
            {`${getResourceNiceName(resource)} in development`}
          </Badge>
        )}
      </Box>
      {/* Tessellating Background Pattern */}
      <Box className={classes.backgroundPattern} style={patternStyle}>
        {[
          ...Array(
            reason !== "in development"
              ? 4 * iconMultiplier
              : 8 * iconMultiplier
          ),
        ].map((_, i) =>
          reason !== "in development" ? (
            <Fragment key={crypto.randomUUID()}>
              <LockRounded
                className={classes.backgroundIcon}
                style={{ fill: theme.colors[subjectColorIndex][5] }}
              />
              <Icon
                className={classes.backgroundIcon}
                style={{ fill: theme.colors[subjectColorIndex][5] }}
              />
            </Fragment>
          ) : (
            <Icon
              key={crypto.randomUUID()}
              className={classes.backgroundIcon}
              style={{ fill: theme.colors[subjectColorIndex][5] }}
            />
          )
        )}
      </Box>
    </>
  );
}

export function getUserUnlockResourceAccessDescriptor(
  session: Session | null,
  verificationStatus: boolean
) {
  if (!session) {
    return "login to unlock";
  } else if (!verificationStatus) {
    return "verify your account to unlock";
  } else if (getUserBillingStatus(session) === "Failed") {
    return "re-attempt your payment to unlock";
  } else {
    return "upgrade to unlock";
  }
}

export function getUserUnlockResourceCTAHref(
  userUnlockResourceAccessDescriptor: ReturnType<
    typeof getUserUnlockResourceAccessDescriptor
  >
) {
  if (userUnlockResourceAccessDescriptor === "verify your account to unlock") {
    return "/profile/account";
  }
  if (
    userUnlockResourceAccessDescriptor === "re-attempt your payment to unlock"
  ) {
    return "/profile/membership";
  }
  if (userUnlockResourceAccessDescriptor === "upgrade to unlock") {
    return "/profile/membership";
  }
  // Default: no session - static login URL for consistent SSR/CSR markup.
  return "/login";
}
